/**
 * 상점 창. 매입가는 표기 가격, 판매가는 buyMul 배.
 */

import Phaser from 'phaser';
import { Sfx } from '../audio/Sfx';
import { ITEMS, TAB_ORDER } from '../data/items';
import { getShop } from '../data/npcs';
import { EV } from '../game/events';
import { gearPrice } from '../systems/gear';
import type { PlayerState } from '../systems/PlayerState';
import { COLOR, TEXT_COLOR, UI_FONT, UI_MONO } from './theme';

const WIDTH = 520;
const HEIGHT = 420;
const PAD = 14;

export class ShopWindow {
  private readonly container: Phaser.GameObjects.Container;
  private readonly rows: Phaser.GameObjects.Container[] = [];
  private readonly mesoText: Phaser.GameObjects.Text;
  private shopId: string | null = null;
  private tab: 'buy' | 'sell' = 'buy';

  constructor(
    scene: Phaser.Scene,
    private readonly state: PlayerState,
  ) {
    const x = scene.scale.width / 2 - WIDTH / 2;
    const y = 64;
    this.container = scene.add.container(x, y).setVisible(false).setDepth(35);

    const g = scene.add.graphics();
    g.fillStyle(COLOR.panelEdge, 0.96);
    g.fillRoundedRect(-4, -4, WIDTH + 8, HEIGHT + 8, 12);
    g.fillStyle(COLOR.window, 0.98);
    g.fillRoundedRect(0, 0, WIDTH, HEIGHT, 10);
    this.container.add(g);

    const title = scene.add.text(PAD, 10, '상점', {
      fontFamily: UI_FONT,
      fontSize: '16px',
      color: TEXT_COLOR.dark,
    });
    const close = scene.add
      .text(WIDTH - PAD, 12, '✕', {
        fontFamily: UI_MONO,
        fontSize: '14px',
        color: TEXT_COLOR.dark,
      })
      .setOrigin(1, 0)
      .setInteractive({ useHandCursor: true });
    close.on('pointerdown', () => this.close());

    const buyTab = scene.add
      .text(PAD, 36, '사기', {
        fontFamily: UI_MONO,
        fontSize: '13px',
        color: TEXT_COLOR.dark,
        backgroundColor: '#d8c8a8',
        padding: { x: 8, y: 3 },
      })
      .setInteractive({ useHandCursor: true });
    const sellTab = scene.add
      .text(PAD + 70, 36, '팔기', {
        fontFamily: UI_MONO,
        fontSize: '13px',
        color: TEXT_COLOR.dark,
        backgroundColor: '#d8c8a8',
        padding: { x: 8, y: 3 },
      })
      .setInteractive({ useHandCursor: true });
    buyTab.on('pointerdown', () => {
      this.tab = 'buy';
      this.refresh();
    });
    sellTab.on('pointerdown', () => {
      this.tab = 'sell';
      this.refresh();
    });

    this.mesoText = scene.add.text(PAD, HEIGHT - 28, '', {
      fontFamily: UI_FONT,
      fontSize: '13px',
      color: TEXT_COLOR.darkDim,
      wordWrap: { width: WIDTH - PAD * 2, useAdvancedWrap: true },
    });
    this.container.add([title, close, buyTab, sellTab, this.mesoText]);

    this.state.events.on(EV.mesoChanged, this.refresh, this);
    this.state.inventory.events.on(EV.inventoryChanged, this.refresh, this);
    scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.state.events.off(EV.mesoChanged, this.refresh, this);
      this.state.inventory.events.off(EV.inventoryChanged, this.refresh, this);
    });
  }

  open(shopId: string): void {
    this.shopId = shopId;
    this.tab = 'buy';
    this.container.setVisible(true);
    this.refresh();
  }

  close(): void {
    this.container.setVisible(false);
    this.shopId = null;
  }

  get visible(): boolean {
    return this.container.visible;
  }

  private refresh = (): void => {
    if (!this.visible || !this.shopId) return;
    const shop = getShop(this.shopId);
    if (!shop) return;
    this.mesoText.setText(`메소 ${this.state.meso.toLocaleString()}  ·  클릭으로 ${this.tab === 'buy' ? '구매' : '판매'}`);
    for (const row of this.rows) row.destroy();
    this.rows.length = 0;

    const scene = this.container.scene;
    if (this.tab === 'buy') {
      shop.stock.forEach((itemId, i) => {
        const item = ITEMS[itemId];
        if (!item) return;
        const price = Math.max(1, item.price * shop.buyMul);
        const y = 64 + i * 36;
        const row = scene.add.container(0, y);
        const label = scene.add
          .text(PAD, 0, `${item.name}   ${price.toLocaleString()} 메소`, {
            fontFamily: UI_FONT,
            fontSize: '14px',
            color: TEXT_COLOR.dark,
            wordWrap: { width: WIDTH - PAD * 2, useAdvancedWrap: true },
          })
          .setInteractive({ useHandCursor: true });
        label.on('pointerdown', () => {
          if (this.state.buyFromShop(shop.id, itemId)) Sfx.uiClick();
          else Sfx.deny();
        });
        row.add(label);
        this.container.add(row);
        this.rows.push(row);
      });
      return;
    }

    let i = 0;
    for (const tab of TAB_ORDER) {
      this.state.inventory.slots(tab).forEach((slot, index) => {
        if (!slot) return;
        const item = ITEMS[slot.itemId];
        if (!item) return;
        const y = 64 + i * 32;
        i += 1;
        const row = scene.add.container(0, y);
        const sellAt = slot.gear ? gearPrice(slot.gear) : item.price;
        const label = scene.add
          .text(PAD, 0, `${item.name} ×${slot.count}   매각 ${sellAt.toLocaleString()}`, {
            fontFamily: UI_FONT,
            fontSize: '13px',
            color: TEXT_COLOR.dark,
            wordWrap: { width: WIDTH - PAD * 2, useAdvancedWrap: true },
          })
          .setInteractive({ useHandCursor: true });
        label.on('pointerdown', () => {
          if (this.state.sellSlot(tab, index)) Sfx.uiClick();
          else Sfx.deny();
        });
        row.add(label);
        this.container.add(row);
        this.rows.push(row);
      });
    }
  };
}
